/* eslint-disable */
import { useMemo, useState } from 'react';
import { runApiFetch, tableBuilders } from './createOrdersApiConfig';
import { notificationServices } from '@/Services';

export const FIXED_IDS = ['S.No', 'Item Name', 'SkuCode'];
export const META_IDS = [...FIXED_IDS, 'Total', 'Actions'];

export const watchedFields = (step) => (
	step?.refetchWhenChanged || step?.onEnterFetch?.refetchWhenChanged || []
);

export const fetchKeyOf = (fields, state) => (fields || [])
	.map((fieldId) => JSON.stringify(state?.[fieldId] ?? null))
	.join('|');

export const stepFetchKey = (step, state) =>
	fetchKeyOf(watchedFields(step), state);

export const fetchStepTable = async (step, { state, lists, productList }) => {
	const fetchCfg = step?.onEnterFetch;
	if (!fetchCfg) return null;
	const headerData = await runApiFetch(fetchCfg.header, {
		state,
		lists,
		productList,
	});
	const headers = headerData?.headers || [];
	const stores = headerData?.apiStoreList || [];
	const rows = (await runApiFetch(fetchCfg.row, {
		state,
		lists,
		productList,
		stores,
	})) || [];
	return {
		headers,
		rows,
		stores,
		orderIds: headerData?.orderIds || [],
		notice: headerData?.notice || null,
		fetchKey: stepFetchKey(step, state),
	};
};
export const useStepTable = ({
	step2,
	state,
	lists,
	productList,
	tableData,
	setTableData,
}) => {
	const [tableLoading, setTableLoading] = useState(false);

	const buildStaticTable = () => {
		const builder = tableBuilders?.[step2?.staticTable];
		if (!builder) return;
		setTableData?.((prev) =>
			builder({ state, lists, prevRows: prev?.rows || [] })
		);
	};

	const fetchTable = async () => {
		setTableLoading(true);
		try {
			const next = await fetchStepTable(step2, { state, lists, productList });
			if (next?.notice) {
				notificationServices.generateNotification({
					type: next.stores?.length ? 'warning' : 'error',
					message: next.notice,
				});
			}
			if (next) setTableData?.(next);
		} catch (error) {
			console.error('Error fetching table:', error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'Could not load the products table. Please retry.',
			});
		} finally {
			setTableLoading(false);
		}
	};

	const enterTable = () => {
		if (tableData?.headers?.length > 0) return;
		if (step2?.staticTable) {
			buildStaticTable();
			return;
		}
		if (step2?.onEnterFetch) fetchTable();
	};

	const showGroupedHeaders = step2?.tableConfig?.showGroupedHeaders === true;

	const cellStores = useMemo(
		() =>
			(tableData?.headers || [])
				.filter((header) => !META_IDS.includes(header.id) &&
					!header._unknownColumn)
				.map(
					(header) =>
						tableData?.stores?.find(
							(store) => `${store?.id}` === `${header.id}`
						) || { id: header.id }
				),
		[tableData?.headers, tableData?.stores]
	);

	const tableColumns = useMemo(
		() =>
			(tableData?.headers || [])
				.filter((header) => header.id !== 'SkuCode')
				.map((header) => ({
					key: String(header.id),
					title: header.id === 'Item Name' ? 'Product Name' : header.name,
					width:
						header.id === 'S.No'
							? 56
							: header.id === 'Item Name'
								? 260
								: header.id === 'Total'
									? 90
									: header.id === 'Actions'
										? 70
										: 130,
					fixed: FIXED_IDS.includes(header.id),
					customCol: true,
					storeName: header.storeName,
					shortName: header.shortName,
					unknown: header._unknownColumn === true,
					...(showGroupedHeaders && header.groupName
						? { group: header.groupName }
						: {}),
					...(showGroupedHeaders &&
						!header.groupName &&
						FIXED_IDS.includes(header.id) &&
						step2?.tableConfig?.coreGroupLabel
						? { group: step2.tableConfig.coreGroupLabel }
						: {}),
					...(showGroupedHeaders &&
						!header.groupName &&
						['Total', 'Actions'].includes(header.id) &&
						step2?.tableConfig?.summaryGroupLabel
						? { group: step2.tableConfig.summaryGroupLabel }
						: {}),
				})),
		[tableData?.headers, showGroupedHeaders, step2?.tableConfig]
	);

	const tableGroups = useMemo(() => {
		if (!showGroupedHeaders) return {};
		const map = {};
		const groupStyle = {
			color: step2?.tableConfig?.groupHeaderColor,
			textColor: step2?.tableConfig?.groupHeaderTextColor,
		};
		if (step2?.tableConfig?.coreGroupLabel) {
			map[step2.tableConfig.coreGroupLabel] = groupStyle;
		}
		if (step2?.tableConfig?.summaryGroupLabel) {
			map[step2.tableConfig.summaryGroupLabel] = groupStyle;
		}
		(tableData?.headers || []).forEach((header) => {
			if (header.groupName && !map[header.groupName]) {
				map[header.groupName] = {
					...groupStyle,
					...(header.clientBuyerId != null && header.clientBuyerId !== ''
						? { subLabel: `Client Buyer Code · ${header.clientBuyerId}` }
						: {}),
				};
			}
		});
		return map;
	}, [tableData?.headers, showGroupedHeaders, step2?.tableConfig]);

	return {
		tableLoading,
		enterTable,
		cellStores,
		tableColumns,
		tableGroups,
	};
};

export default useStepTable;
